import { createSlice } from '@reduxjs/toolkit';
import { createAppThunk } from '../createAppThunk';
import { fetchInstructionAssignmentsByEmployee } from '@/lib/apiClient';
import { toAppError } from '@/lib/toAppError';
import type { InstructionAssignmentResponseDto } from '@/types/Api';
import type { NormalizedError } from '@/types/NormalizedError';

interface InstructionAssignmentsState {
  assignments: InstructionAssignmentResponseDto[];
  byEmployee: Record<string, InstructionAssignmentResponseDto[]>;
  loading: boolean;
  loadingByEmployee: Record<string, boolean>;
  error: NormalizedError | null;
  errorByEmployee: Record<string, NormalizedError | null>;
}

const initialState: InstructionAssignmentsState = {
  assignments: [],
  byEmployee: {},
  loading: false,
  loadingByEmployee: {},
  error: null,
  errorByEmployee: {},
};

export const getInstructionAssignments = createAppThunk<
  InstructionAssignmentResponseDto[],
  { organizationId: string; token: string; employeeId?: string }
>(
  'instructionAssignments/getInstructionAssignments',
  async ({ organizationId, token, employeeId }, thunkAPI) => {
    try {
      const response = await fetchInstructionAssignmentsByEmployee(
        organizationId,
        employeeId,
        token,
      );
      return (response.data ?? []) as InstructionAssignmentResponseDto[];
    } catch {
      return thunkAPI.rejectWithValue(
        toAppError(new Error('Failed to fetch instruction assignments')),
      );
    }
  },
);

const instructionAssignmentsSlice = createSlice({
  name: 'instructionAssignments',
  initialState,
  reducers: {
    clearInstructionAssignments: (state) => {
      state.assignments = [];
      state.byEmployee = {};
      state.error = null;
      state.errorByEmployee = {};
      state.loading = false;
      state.loadingByEmployee = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInstructionAssignments.pending, (state, action) => {
        const employeeId = action.meta.arg.employeeId;
        if (employeeId) {
          state.loadingByEmployee[employeeId] = true;
          state.errorByEmployee[employeeId] = null;
          return;
        }

        state.loading = true;
        state.error = null;
      })
      .addCase(getInstructionAssignments.fulfilled, (state, action) => {
        const employeeId = action.meta.arg.employeeId;
        if (employeeId) {
          state.loadingByEmployee[employeeId] = false;
          state.errorByEmployee[employeeId] = null;
          state.byEmployee[employeeId] = action.payload;
          return;
        }

        state.loading = false;
        state.assignments = action.payload;
      })
      .addCase(getInstructionAssignments.rejected, (state, action) => {
        const employeeId = action.meta.arg.employeeId;
        if (employeeId) {
          state.loadingByEmployee[employeeId] = false;
          state.errorByEmployee[employeeId] = action.payload ?? null;
          state.byEmployee[employeeId] = [];
          return;
        }

        state.loading = false;
        state.error = action.payload ?? null;
        state.assignments = [];
      });
  },
});

export const { clearInstructionAssignments } = instructionAssignmentsSlice.actions;
export default instructionAssignmentsSlice.reducer;
